// RUTA: src/lib/createTicket.ts
//
// Helper UNIVERSAL para que cualquier parte del sistema pueda crear
// un ticket de soporte con 1 línea de código:
//
//   const ticket = await createTicketAuto({
//     title: 'Falló al cargar las rutinas',
//     description: 'El usuario intentó X pero recibió Y',
//   });
//
// Auto-captura:
//   - URL actual, user agent, app version
//   - Rol del usuario, email
//   - Screenshot del estado actual de la página (html2canvas)
//
// Esto saca al usuario de la fricción de "abre el panel de soporte,
// llena todo el form" — un click y todo el contexto va al admin.

import html2canvas from 'html2canvas';
import { supabase } from '@/integrations/supabase/client';
import { captureContext, uploadTicketAttachment, type TicketCategory } from './supportTickets';

type TicketPriority = 'P1' | 'P2' | 'P3' | 'P4';

interface CreateTicketOpts {
  /** Título corto del ticket (ej. "Falló analizar comida con IA") */
  title: string;
  /** Descripción detallada (puede incluir markdown). Si no se pasa,
   *  el ticket se crea solo con el título y los contextos automáticos. */
  description?: string;
  /** Categoría — default 'bug' (la mayoría de tickets auto-creados
   *  son bugs). */
  category?: TicketCategory;
  /** Si true, intenta capturar screenshot de toda la página actual.
   *  Default true. Falla silenciosamente si html2canvas no puede. */
  captureScreenshot?: boolean;
  /** Elemento DOM específico a capturar. Si se pasa, captura solo ese
   *  elemento en vez de todo el body. Útil cuando el error ocurre en
   *  una sección y queremos evidencia del contexto local. */
  screenshotElement?: HTMLElement | null;
  /** Datos extra para incluir en la descripción como bloque técnico
   *  (ej. error.stack, request payload, etc.). Se serializan como
   *  JSON dentro de un fenced code block. */
  extra?: Record<string, unknown>;
  /** Override manual de prioridad. Si no se pasa, se auto-clasifica
   *  según category, presencia de stack trace y conteo de recargas
   *  limpias en la URL (`?_clr=N`). */
  priority?: TicketPriority;
}

/**
 * Clasificación ITIL automática del ticket en el momento de creación.
 *
 *   P1 (crítica): la app no se puede usar — el cliente ya intentó 3+
 *                 recargas limpias (`?_clr=3+`) y sigue fallando. Es
 *                 un bug del código, requiere atención inmediata.
 *   P2 (alta):    crash auto-capturado por error boundary, o problema
 *                 de pago. Bloquea al usuario pero puede haber
 *                 workaround (recargar, reintentar).
 *   P3 (media):   bug reportado manualmente por el usuario sin
 *                 evidencia de crash. Probablemente afecta uso pero
 *                 no bloquea.
 *   P4 (baja):    sugerencias, preguntas, mejoras. No urgente.
 *
 * El admin puede sobrescribir manualmente desde el panel después.
 */
function autoClassifyPriority(
  category: TicketCategory,
  hasStackTrace: boolean,
): TicketPriority {
  // 1. Recargas limpias agotadas → siempre P1, sin importar categoría.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const clrAttempts = parseInt(params.get('_clr') || '0', 10);
    if (clrAttempts >= 3) return 'P1';
  }

  // 2. Categorías no-bug van por su naturaleza.
  if (category === 'suggestion' || category === 'question') return 'P4';
  if (category === 'payment') return 'P2';

  // 3. Bugs: con stack trace = crash auto-capturado = P2;
  //    sin stack = reporte manual del usuario = P3.
  if (category === 'bug') return hasStackTrace ? 'P2' : 'P3';

  // 4. 'other' u otras → P3 default.
  return 'P3';
}

interface CreatedTicket {
  id: number;
  ticket_number: string;
}

/**
 * Crea un ticket de soporte con todo el contexto técnico capturado
 * automáticamente. No abre ningún modal — sucede en background y
 * devuelve el ticket creado para que el llamador pueda mostrar
 * confirmación al usuario.
 *
 * Devuelve null si falla (sesión expirada, sin red, etc.). El
 * llamador decide cómo manejar el null (toast de error, pedir login).
 */
export async function createTicketAuto(opts: CreateTicketOpts): Promise<CreatedTicket | null> {
  const {
    title,
    description = '',
    category = 'bug',
    captureScreenshot = true,
    screenshotElement,
    extra,
    priority: priorityOverride,
  } = opts;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Pre-fill role + email del onboarding (best-effort)
    const [{ data: roleRow }, { data: ob }] = await Promise.all([
      supabase.from('user_roles').select('role').eq('user_id', user.id).limit(1).maybeSingle(),
      supabase.from('client_onboarding_profiles').select('email').eq('user_id', user.id).maybeSingle(),
    ]);

    // Construir descripción final con extras técnicos
    let finalDescription = description.trim();
    if (extra && Object.keys(extra).length > 0) {
      const extraBlock = '```json\n' + JSON.stringify(extra, null, 2) + '\n```';
      finalDescription = finalDescription
        ? `${finalDescription}\n\n**Contexto técnico:**\n${extraBlock}`
        : `**Contexto técnico:**\n${extraBlock}`;
    }

    // Auto-clasificación de prioridad ITIL. El override manual gana
    // si el llamador lo pasó explícitamente.
    const hasStackTrace = !!(extra && typeof extra.stack === 'string');
    const priority = priorityOverride || autoClassifyPriority(category, hasStackTrace);

    const ctx = captureContext();
    const { data: ticket, error } = await supabase
      .from('support_tickets')
      .insert({
        user_id: user.id,
        category,
        title,
        description: finalDescription || null,
        priority,
        ...ctx,
        context_user_role: roleRow?.role || 'client',
        context_user_email: ob?.email || user.email || null,
      })
      .select('id, ticket_number, priority')
      .single();
    if (error || !ticket) return null;

    // Best-effort screenshot — si falla, el ticket queda igual sin foto
    if (captureScreenshot && typeof document !== 'undefined') {
      try {
        const target = screenshotElement || document.body;
        const canvas = await html2canvas(target, {
          backgroundColor: null,
          scale: 1,
          logging: false,
          useCORS: true,
          // Limitar a viewport para que no genere PNGs gigantescos
          windowWidth: Math.min(document.documentElement.clientWidth, 1280),
          windowHeight: Math.min(document.documentElement.clientHeight, 1600),
        });
        const blob = await new Promise<Blob | null>(resolve =>
          canvas.toBlob(b => resolve(b), 'image/png', 0.85),
        );
        if (blob) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
          await uploadTicketAttachment(ticket.id, file);
        }
      } catch (err) {
        console.warn('Screenshot capture falló (ticket creado sin imagen):', err);
      }
    }

    // Notificar al admin (push + email) — fire and forget
    supabase.functions.invoke('notify-ticket', {
      body: { ticket_id: ticket.id, event: 'created' },
    }).catch(() => {});

    return { id: ticket.id, ticket_number: ticket.ticket_number };
  } catch (err) {
    console.error('createTicketAuto falló:', err);
    return null;
  }
}

/**
 * Versión "fire-and-forget" para reportar un error técnico que el
 * usuario no necesariamente vió. Útil para fallos en background
 * (sync, push subscription, etc.). Devuelve la promesa por si el
 * llamador quiere mostrar feedback opcional.
 *
 *   reportError(err, { contextLabel: 'sync queue', extra: { ... } });
 */
export async function reportError(
  err: unknown,
  opts: {
    contextLabel: string;
    extra?: Record<string, unknown>;
  },
): Promise<CreatedTicket | null> {
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack : undefined;
  return createTicketAuto({
    title: `[Auto] ${opts.contextLabel}: ${message.slice(0, 80)}`,
    description: `Error capturado automáticamente.\n\n**Mensaje:** ${message}\n\n**Contexto:** ${opts.contextLabel}`,
    category: 'bug',
    captureScreenshot: true,
    extra: { ...opts.extra, stack },
  });
}
